// src/components/ScoreboardUser/ScoreboardUser.jsx
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import DetermineWinnerModal from "./DetermineWinnerModal";
import {
  FaBell,
  FaCompress,
  FaExpand,
  FaFlagCheckered,
  FaMinus,
  FaPlay,
  FaPlus,
  FaRedo,
  FaSlidersH,
  FaStop,
  FaTimes,
} from "react-icons/fa";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const LONG_PRESS_MS = 550;

// À adapter si ton logo public a un autre chemin.
const APP_LOGO_URL = "/logo_monapplitournoi_192.png";

/**
 * Bouton pressable robuste :
 * - clic court => onShortPress
 * - appui long => onLongPress
 * - l'appui long ne déclenche jamais le clic court au relâchement
 * - utilisation des Pointer Events uniquement pour éviter touch + mouse en double
 */
function Pressable({
  children,
  className = "",
  title = "",
  ariaLabel = "",
  onShortPress,
  onLongPress,
  disabled = false,
}) {
  const pressRef = useRef(null);
  const longPressRef = useRef(false);
  const touchActiveRef = useRef(false);

  const clearPressTimer = useCallback(() => {
    if (pressRef.current) {
      clearTimeout(pressRef.current);
      pressRef.current = null;
    }
  }, []);

  const startLongPress = useCallback(() => {
    if (disabled) return;

    longPressRef.current = false;

    clearPressTimer();

    pressRef.current = setTimeout(() => {
      longPressRef.current = true;
      onLongPress?.();
    }, LONG_PRESS_MS);
  }, [clearPressTimer, disabled, onLongPress]);

  const endLongPress = useCallback(() => {
    clearPressTimer();
  }, [clearPressTimer]);

  const handleClick = useCallback(
    (event) => {
      event.preventDefault();
      event.stopPropagation();

      if (disabled) return;

      if (!longPressRef.current) {
        onShortPress?.();
      }

      // Important :
      // on remet le flag à false seulement après le cycle du click,
      // sinon certains navigateurs relancent un +1 au relâchement.
      setTimeout(() => {
        longPressRef.current = false;
      }, 80);
    },
    [disabled, onShortPress],
  );

  useEffect(() => {
    return () => clearPressTimer();
  }, [clearPressTimer]);

  return (
    <button
      type="button"
      title={title}
      aria-label={ariaLabel || title}
      disabled={disabled}
      onClick={handleClick}
      onMouseDown={() => {
        if (touchActiveRef.current) return;
        startLongPress();
      }}
      onMouseUp={endLongPress}
      onMouseLeave={endLongPress}
      onTouchStart={() => {
        touchActiveRef.current = true;
        startLongPress();
      }}
      onTouchEnd={() => {
        endLongPress();

        setTimeout(() => {
          touchActiveRef.current = false;
        }, 400);
      }}
      onTouchCancel={() => {
        endLongPress();

        setTimeout(() => {
          touchActiveRef.current = false;
        }, 400);
      }}
      onContextMenu={(e) => e.preventDefault()}
      className={`select-none touch-none outline-none focus:outline-none focus-visible:outline-none focus:ring-0 disabled:cursor-not-allowed disabled:opacity-50 ${className}`}
    >
      {children}
    </button>
  );
}

const ScoreboardUser = ({
  settings,
  competiteur1,
  competiteur2,
  soundKey = "default",
}) => {
  const publicCompId = "public";
  const tatamiId = soundKey || "default";

  const rootRef = useRef(null);

  const redCompetitor = competiteur1 || {
    nom_competiteur: "Judoka Rouge",
    prenom_competiteur: "",
    club_competiteur: "",
  };

  const whiteCompetitor = competiteur2 || {
    nom_competiteur: "Judoka Blanc",
    prenom_competiteur: "",
    club_competiteur: "",
  };

  const tapisLabel = String(settings?.tapis || "1").trim() || "1";
  const baseTime = Number(settings?.temps) || 180;

  const [score1, setScore1] = useState({ ippon: 0, wazari: 0, yuko: 0 });
  const [score2, setScore2] = useState({ ippon: 0, wazari: 0, yuko: 0 });

  const [penalites1, setPenalites1] = useState(0);
  const [penalites2, setPenalites2] = useState(0);

  const [osaekomi1, setOsaekomi1] = useState(0);
  const [osaekomi2, setOsaekomi2] = useState(0);

  const [osaekomiRunning1, setOsaekomiRunning1] = useState(false);
  const [osaekomiRunning2, setOsaekomiRunning2] = useState(false);

  const [chrono, setChrono] = useState(baseTime);
  const [chronoMax, setChronoMax] = useState(baseTime);
  const [isChronoRunning, setIsChronoRunning] = useState(false);

  const [goldenScoreMode, setGoldenScoreMode] = useState(false);
  const [goldenChrono, setGoldenChrono] = useState(0);

  const [wazariAwaseteIppon, setWazariAwaseteIppon] = useState(
    settings?.wazariAwaseteIppon ?? true,
  );
  const [ipponMetFin, setIpponMetFin] = useState(settings?.finIppon ?? true);
  const [deuxIpponFin, setDeuxIpponFin] = useState(
    settings?.finDeuxIppon ?? false,
  );
  const [pointsMetFin, setPointsMetFin] = useState(
    settings?.finPoints ?? false,
  );
  const [pointsFin, setPointsFin] = useState(
    settings?.finPoints ? Number(settings?.pointsFin) || null : null,
  );

  const [modalVisible, setModalVisible] = useState(false);
  const [forfaitActif1, setForfaitActif1] = useState(false);
  const [forfaitActif2, setForfaitActif2] = useState(false);

  const [controlsOpen, setControlsOpen] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const chronoEndedRef = useRef(false);
  const autoEndRef = useRef(false);

  /**
   * Sert uniquement à savoir quelle valeur a été donnée automatiquement par
   * l'osaekomi afin de pouvoir la remplacer correctement :
   * 5s  : +1 Yuko
   * 10s : -1 Yuko, +1 Waza-ari
   * 20s : -1 Waza-ari, +1 Ippon
   */
  const osaeAwardRef = useRef({
    1: { yuko: false, wazari: false, ippon: false },
    2: { yuko: false, wazari: false, ippon: false },
  });

  const formatTime = useCallback((seconds) => {
    const safe = Math.max(0, Number(seconds) || 0);
    const mins = Math.floor(safe / 60)
      .toString()
      .padStart(2, "0");
    const secs = (safe % 60).toString().padStart(2, "0");
    return `${mins}:${secs}`;
  }, []);

  const scoreTotal = useCallback((score) => {
    return (
      (Number(score?.ippon) || 0) * 10 +
      (Number(score?.wazari) || 0) * 7 +
      (Number(score?.yuko) || 0) * 5
    );
  }, []);

  const clampScore = useCallback((value) => {
    return Math.max(0, Number(value) || 0);
  }, []);

  const playGongSound = useCallback(() => {
    try {
      const raw = localStorage.getItem(`gongSound:${publicCompId}:${tatamiId}`);
      let src = "/son/gong.mp3";

      if (raw) {
        const parsed = JSON.parse(raw);

        if (parsed?.value === "custom" && parsed?.url) {
          src = String(parsed.url).trim() || src;
        } else if (parsed?.value && parsed.value !== "custom") {
          src = String(parsed.value);
        }
      }

      const audio = new Audio(src);
      audio.play().catch(() => {
        const fallback = new Audio("/son/gong.mp3");
        fallback.play().catch((e) => {
          console.error("Lecture gong fallback échouée :", e);
        });
      });
    } catch (e) {
      console.error("Lecture gong échouée :", e);
      const fallback = new Audio("/son/gong.mp3");
      fallback.play().catch((err) => {
        console.error("Lecture gong fallback échouée :", err);
      });
    }
  }, [tatamiId]);

  const stopAllOsaekomi = useCallback(() => {
    setOsaekomiRunning1(false);
    setOsaekomiRunning2(false);
  }, []);

  const stopFightWithoutModal = useCallback(
    (message, type = "info") => {
      if (autoEndRef.current) return;

      autoEndRef.current = true;
      setIsChronoRunning(false);
      stopAllOsaekomi();

      if (type === "success") toast.success(message);
      else if (type === "warn") toast.warn(message);
      else if (type === "error") toast.error(message);
      else toast.info(message);
    },
    [stopAllOsaekomi],
  );

  /**
   * Modification manuelle des scores.
   * Ici on garde la règle Waza-ari awasete Ippon pour les clics directs.
   */
  const updateScore = useCallback(
    (fighter, key, delta) => {
      const setter = fighter === 1 ? setScore1 : setScore2;

      setter((prev) => {
        const current = clampScore(prev?.[key]);
        const nextValue = Math.max(0, current + delta);

        let next = {
          ...prev,
          [key]: nextValue,
        };

        if (
          key === "wazari" &&
          delta > 0 &&
          wazariAwaseteIppon &&
          nextValue >= 2
        ) {
          next = {
            ...next,
            wazari: 0,
            ippon: clampScore(next.ippon) + 1,
          };

          toast.info("Waza-ari awasete Ippon 🥋");
        }

        return next;
      });
    },
    [clampScore, wazariAwaseteIppon],
  );

  /**
   * Modification directe utilisée par l'osaekomi.
   * Elle ne déclenche pas Waza-ari awasete Ippon, car l'osaekomi doit remplacer
   * la valeur précédente : Yuko -> Waza-ari -> Ippon.
   */
  const applyOsaekomiAward = useCallback(
    (fighter, stage) => {
      const setter = fighter === 1 ? setScore1 : setScore2;
      const award = osaeAwardRef.current[fighter];

      let convertedToIppon = false;

      setter((prev) => {
        const next = {
          ...prev,
          ippon: Math.max(0, Number(prev?.ippon || 0)),
          wazari: Math.max(0, Number(prev?.wazari || 0)),
          yuko: Math.max(0, Number(prev?.yuko || 0)),
        };

        // 5 secondes : on ajoute uniquement le Yuko d'osaekomi.
        if (stage === "yuko") {
          next.yuko += 1;
        }

        // 10 secondes : le Waza-ari remplace le Yuko donné à 5s.
        if (stage === "wazari") {
          if (award.yuko) {
            next.yuko = Math.max(0, next.yuko - 1);
          }

          next.wazari += 1;

          // Cas important :
          // s'il y avait déjà un Waza-ari, le Waza-ari d'osaekomi peut faire
          // Waza-ari awasete Ippon.
          if (wazariAwaseteIppon && next.wazari >= 2) {
            next.wazari = Math.max(0, next.wazari - 2);
            next.ippon += 1;
            convertedToIppon = true;

            // On considère que l'osaekomi a déjà abouti à Ippon via awasete.
            award.ippon = true;
          }
        }

        // 20 secondes : l'Ippon remplace le Waza-ari donné à 10s.
        if (stage === "ippon") {
          if (award.wazari) {
            next.wazari = Math.max(0, next.wazari - 1);
          } else if (award.yuko) {
            next.yuko = Math.max(0, next.yuko - 1);
          }

          next.ippon += 1;
        }

        return next;
      });

      return convertedToIppon;
    },
    [wazariAwaseteIppon],
  );

  const updatePenalty = useCallback((fighter, delta) => {
    const setter = fighter === 1 ? setPenalites1 : setPenalites2;

    setter((prev) => {
      const current = Number(prev || 0);
      return Math.max(0, Math.min(3, current + delta));
    });
  }, []);

  const resetOsaekomi = useCallback((fighter) => {
    if (fighter === 1) {
      setOsaekomiRunning1(false);
      setOsaekomi1(0);
    } else {
      setOsaekomiRunning2(false);
      setOsaekomi2(0);
    }

    osaeAwardRef.current[fighter] = {
      yuko: false,
      wazari: false,
      ippon: false,
    };
  }, []);

  const toggleOsaekomi = useCallback((fighter) => {
    if (fighter === 1) {
      setOsaekomiRunning1((v) => {
        const next = !v;
        if (next) setOsaekomiRunning2(false);
        return next;
      });
    } else {
      setOsaekomiRunning2((v) => {
        const next = !v;
        if (next) setOsaekomiRunning1(false);
        return next;
      });
    }
  }, []);

  const resetFight = useCallback(() => {
    setScore1({ ippon: 0, wazari: 0, yuko: 0 });
    setScore2({ ippon: 0, wazari: 0, yuko: 0 });

    setPenalites1(0);
    setPenalites2(0);

    setOsaekomi1(0);
    setOsaekomi2(0);
    setOsaekomiRunning1(false);
    setOsaekomiRunning2(false);

    setChrono(chronoMax);
    setIsChronoRunning(false);

    setGoldenScoreMode(false);
    setGoldenChrono(0);

    setForfaitActif1(false);
    setForfaitActif2(false);
    setModalVisible(false);

    chronoEndedRef.current = false;
    autoEndRef.current = false;

    osaeAwardRef.current = {
      1: { yuko: false, wazari: false, ippon: false },
      2: { yuko: false, wazari: false, ippon: false },
    };
  }, [chronoMax]);

  const adjustChrono = useCallback((delta) => {
    setChrono((prev) => Math.max(0, Number(prev || 0) + delta));
    chronoEndedRef.current = false;
    autoEndRef.current = false;
  }, []);

  const toggleMainChrono = useCallback(() => {
    setIsChronoRunning((v) => !v);
  }, []);

  const toggleFullscreen = useCallback(async () => {
    try {
      if (!document.fullscreenElement) {
        await rootRef.current?.requestFullscreen?.();
      } else {
        await document.exitFullscreen?.();
      }
    } catch (e) {
      console.error("Impossible de changer le mode plein écran :", e);
      toast.error("Le mode plein écran n'a pas pu être activé.");
    }
  }, []);

  const handleForfait = useCallback(
    (fighter) => {
      autoEndRef.current = true;

      if (fighter === 1) {
        if (forfaitActif1) return;

        setScore1({ ippon: 0, wazari: 0, yuko: 0, forfait: true });
        setScore2((p) => ({
          ...p,
          ippon: clampScore(p.ippon) + 1,
        }));
        setForfaitActif1(true);
        toast.warn("Forfait rouge");
      } else {
        if (forfaitActif2) return;

        setScore2({ ippon: 0, wazari: 0, yuko: 0, forfait: true });
        setScore1((p) => ({
          ...p,
          ippon: clampScore(p.ippon) + 1,
        }));
        setForfaitActif2(true);
        toast.warn("Forfait blanc");
      }

      setIsChronoRunning(false);
      stopAllOsaekomi();
    },
    [clampScore, forfaitActif1, forfaitActif2, stopAllOsaekomi],
  );

  /**
   * Application des réglages.
   * Ne dépend surtout pas de isChronoRunning, sinon un stop du chrono peut
   * remettre le chrono à sa valeur initiale.
   */
  useEffect(() => {
    const newBase = Number(settings?.temps) || 180;

    setChronoMax(newBase);
    setChrono(newBase);

    setWazariAwaseteIppon(settings?.wazariAwaseteIppon ?? true);
    setIpponMetFin(settings?.finIppon ?? true);
    setDeuxIpponFin(settings?.finDeuxIppon ?? false);
    setPointsMetFin(settings?.finPoints ?? false);
    setPointsFin(
      settings?.finPoints ? Number(settings?.pointsFin) || null : null,
    );

    chronoEndedRef.current = false;
    autoEndRef.current = false;
  }, [settings]);

  useEffect(() => {
    setScore1((prev) => ({ ...prev, hansokuMake: penalites1 >= 3 }));
  }, [penalites1]);

  useEffect(() => {
    setScore2((prev) => ({ ...prev, hansokuMake: penalites2 >= 3 }));
  }, [penalites2]);

  useEffect(() => {
    if (penalites1 >= 3) {
      stopFightWithoutModal("Hansoku-make rouge par 3 pénalités", "warn");
    }
  }, [penalites1, stopFightWithoutModal]);

  useEffect(() => {
    if (penalites2 >= 3) {
      stopFightWithoutModal("Hansoku-make blanc par 3 pénalités", "warn");
    }
  }, [penalites2, stopFightWithoutModal]);

  useEffect(() => {
    const onFullscreenChange = () => {
      setIsFullscreen(document.fullscreenElement === rootRef.current);
    };

    document.addEventListener("fullscreenchange", onFullscreenChange);

    return () => {
      document.removeEventListener("fullscreenchange", onFullscreenChange);
    };
  }, []);

  /**
   * Barre d'espace : start / stop du chrono.
   * On empêche le comportement natif du bouton pour éviter le cadre de focus.
   */
  useEffect(() => {
    const onKeyDown = (event) => {
      const isSpace =
        event.code === "Space" || event.key === " " || event.key === "Spacebar";

      if (!isSpace || event.repeat) return;

      const target = event.target;
      const tagName = target?.tagName?.toLowerCase?.();

      const isTypingField =
        tagName === "input" ||
        tagName === "textarea" ||
        tagName === "select" ||
        target?.isContentEditable;

      if (isTypingField || modalVisible) return;

      event.preventDefault();
      event.stopPropagation();

      toggleMainChrono();
    };

    window.addEventListener("keydown", onKeyDown, { capture: true });

    return () => {
      window.removeEventListener("keydown", onKeyDown, { capture: true });
    };
  }, [modalVisible, toggleMainChrono]);

  useEffect(() => {
    if (!isChronoRunning) return;

    const interval = setInterval(() => {
      if (goldenScoreMode) {
        setGoldenChrono((prev) => prev + 1);
        return;
      }

      setChrono((prev) => {
        const current = Number(prev) || 0;

        if (current <= 1) {
          if (!chronoEndedRef.current) {
            chronoEndedRef.current = true;
            autoEndRef.current = true;

            playGongSound();
            setIsChronoRunning(false);
            stopAllOsaekomi();
            toast.info("Temps écoulé");
          }

          return 0;
        }

        return current - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [goldenScoreMode, isChronoRunning, playGongSound, stopAllOsaekomi]);

  useEffect(() => {
    if (!osaekomiRunning1) return;

    const interval = setInterval(() => {
      setOsaekomi1((prev) => Math.min(20, Number(prev || 0) + 1));
    }, 1000);

    return () => clearInterval(interval);
  }, [osaekomiRunning1]);

  useEffect(() => {
    if (!osaekomiRunning2) return;

    const interval = setInterval(() => {
      setOsaekomi2((prev) => Math.min(20, Number(prev || 0) + 1));
    }, 1000);

    return () => clearInterval(interval);
  }, [osaekomiRunning2]);

  /**
   * Osaekomi rouge :
   * 5s  => +1 Yuko
   * 10s => -1 Yuko, +1 Waza-ari
   * 20s => -1 Waza-ari, +1 Ippon
   */
  useEffect(() => {
    const award = osaeAwardRef.current[1];

    if (osaekomi1 >= 5 && !award.yuko) {
      award.yuko = true;
      applyOsaekomiAward(1, "yuko");
      toast.info("Osaekomi rouge : Yuko");
    }

    if (osaekomi1 >= 10 && !award.wazari) {
      award.wazari = true;

      const convertedToIppon = applyOsaekomiAward(1, "wazari");

      if (convertedToIppon) {
        setOsaekomiRunning1(false);
        playGongSound();
        toast.success("Osaekomi rouge : Waza-ari awasete Ippon");
        return;
      }

      toast.info("Osaekomi rouge : Waza-ari");
    }

    if (osaekomi1 >= 20 && !award.ippon) {
      award.ippon = true;
      setOsaekomiRunning1(false);

      applyOsaekomiAward(1, "ippon");

      playGongSound();
      toast.success("Osaekomi rouge : Ippon");
    }
  }, [osaekomi1, applyOsaekomiAward, playGongSound]);

  /**
   * Osaekomi blanc :
   * 5s  => +1 Yuko
   * 10s => -1 Yuko, +1 Waza-ari
   * 20s => -1 Waza-ari, +1 Ippon
   */
  useEffect(() => {
    const award = osaeAwardRef.current[2];

    if (osaekomi2 >= 5 && !award.yuko) {
      award.yuko = true;
      applyOsaekomiAward(2, "yuko");
      toast.info("Osaekomi blanc : Yuko");
    }

    if (osaekomi2 >= 10 && !award.wazari) {
      award.wazari = true;

      const convertedToIppon = applyOsaekomiAward(2, "wazari");

      if (convertedToIppon) {
        setOsaekomiRunning2(false);
        playGongSound();
        toast.success("Osaekomi blanc : Waza-ari awasete Ippon");
        return;
      }

      toast.info("Osaekomi blanc : Waza-ari");
    }

    if (osaekomi2 >= 20 && !award.ippon) {
      award.ippon = true;
      setOsaekomiRunning2(false);

      applyOsaekomiAward(2, "ippon");

      playGongSound();
      toast.success("Osaekomi blanc : Ippon");
    }
  }, [osaekomi2, applyOsaekomiAward, playGongSound]);

  useEffect(() => {
    if (!ipponMetFin) return;

    if (score1.ippon >= 1 || score2.ippon >= 1) {
      stopFightWithoutModal("Fin du combat avec Ippon 🥋");
    }
  }, [score1.ippon, score2.ippon, ipponMetFin, stopFightWithoutModal]);

  useEffect(() => {
    if (!deuxIpponFin) return;

    if (score1.ippon >= 2 || score2.ippon >= 2) {
      stopFightWithoutModal("Fin du combat : 2 Ippons atteints 🥋");
    }
  }, [score1.ippon, score2.ippon, deuxIpponFin, stopFightWithoutModal]);

  useEffect(() => {
    if (!pointsMetFin || pointsFin === null) return;

    const totalPoints1 = scoreTotal(score1);
    const totalPoints2 = scoreTotal(score2);

    if (totalPoints1 >= pointsFin || totalPoints2 >= pointsFin) {
      stopFightWithoutModal(
        `Fin du combat : seuil de ${pointsFin} pts atteint 🎯`,
      );
    }
  }, [
    pointsMetFin,
    pointsFin,
    score1,
    score2,
    scoreTotal,
    stopFightWithoutModal,
  ]);

  const chronoProgress = useMemo(() => {
    if (!chronoMax || goldenScoreMode) return 0;
    return Math.min(100, Math.max(0, (chrono / chronoMax) * 100));
  }, [chrono, chronoMax, goldenScoreMode]);

  const FS = {
    topTitle: "text-[clamp(1.8rem,3.8vmin,4.6rem)]",
    topTapis: "text-[clamp(2.6rem,5.6vmin,6.8rem)]",

    nameLast: "text-[clamp(2.4rem,6.4vmin,7.8rem)]",
    nameFirst: "text-[clamp(1.5rem,3.8vmin,4.6rem)]",
    club: "text-[clamp(1.1rem,2.6vmin,3.2rem)]",

    score: "text-[clamp(5.4rem,18vmin,24vh)]",
    scoreMinW: "min-w-[clamp(4.8rem,12vmin,11rem)]",

    osaekomi: "text-[clamp(2.8rem,7.2vmin,8.5rem)]",
    shidoValue: "text-[clamp(2.6rem,6vmin,6.5rem)]",

    chrono: "text-[clamp(6rem,18vmin,22vh)]",
  };

  const ScoreValue = ({ fighter, scoreKey, value, isRed }) => {
    return (
      <Pressable
        title="Clic : +1 • Appui long : -1"
        onShortPress={() => updateScore(fighter, scoreKey, 1)}
        onLongPress={() => updateScore(fighter, scoreKey, -1)}
        className={`${FS.score} ${FS.scoreMinW} rounded-2xl px-2 text-center font-black leading-none tabular-nums transition ${
          isRed
            ? "text-white hover:bg-white/10 active:bg-white/15"
            : "text-black hover:bg-black/10 active:bg-black/15"
        }`}
      >
        {value}
      </Pressable>
    );
  };

  const OsaekomiAndShido = ({
    fighter,
    osaekomi,
    isRunning,
    penalites,
    isRed,
  }) => {
    const osaePercent = Math.min(100, (osaekomi / 20) * 100);

    const osaeCardClass = isRunning
      ? "border-emerald-300 bg-emerald-700 text-white shadow-[0_0_28px_rgba(16,185,129,0.35)]"
      : isRed
        ? "border-white/20 bg-black/15 text-white hover:bg-black/25"
        : "border-black/15 bg-black/5 text-black hover:bg-black/10";

    const osaeTrackClass = isRunning
      ? "bg-emerald-950/55 ring-1 ring-emerald-200/25"
      : isRed
        ? "bg-white/25"
        : "bg-black/15";

    const osaeBarClass = isRunning
      ? "bg-gradient-to-r from-lime-300 via-emerald-300 to-green-400 shadow-[0_0_18px_rgba(134,239,172,0.75)]"
      : "bg-emerald-400";

    return (
      <div className="flex w-[15%] min-w-[150px] flex-col items-stretch justify-center gap-3 overflow-hidden">
        <Pressable
          title="Clic : start/stop osaekomi • Appui long : remise à 0"
          onShortPress={() => toggleOsaekomi(fighter)}
          onLongPress={() => resetOsaekomi(fighter)}
          className={`relative overflow-hidden rounded-2xl border px-3 py-2 text-right shadow-inner transition ${osaeCardClass}`}
        >
          {isRunning && (
            <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_20%_10%,rgba(187,247,208,0.25),transparent_40%)]" />
          )}

          <div className="relative z-10 flex items-center justify-between gap-2">
            <div
              className={`rounded-full px-2 py-0.5 text-[clamp(.65rem,1.1vmin,.85rem)] font-black uppercase tracking-[0.14em] ${
                isRunning
                  ? "bg-emerald-950/35 text-emerald-100 ring-1 ring-emerald-200/25"
                  : isRed
                    ? "bg-white/15 text-white/70"
                    : "bg-black/10 text-black/60"
              }`}
            >
              Osae-komi
            </div>
          </div>

          <div
            className={`relative z-10 mt-1 ${FS.osaekomi} font-black leading-none tabular-nums`}
          >
            {osaekomi}s
          </div>

          <div
            className={`relative z-10 mt-2 h-[clamp(.7rem,1.4vmin,1.2rem)] w-full overflow-hidden rounded ${osaeTrackClass}`}
          >
            <div
              className={`h-full rounded transition-all duration-200 ${osaeBarClass}`}
              style={{ width: `${osaePercent}%` }}
            />
          </div>
        </Pressable>

        <Pressable
          title="Clic : +1 Shido • Appui long : -1 Shido"
          onShortPress={() => updatePenalty(fighter, 1)}
          onLongPress={() => updatePenalty(fighter, -1)}
          className={`rounded-2xl border px-3 py-2 transition ${
            penalites >= 3
              ? "border-red-300 bg-red-700 text-white"
              : "border-yellow-300/70 bg-yellow-300 text-black hover:bg-yellow-200"
          }`}
        >
          <div className="flex items-center justify-between gap-3">
            <div className="text-[clamp(1.2rem,2.6vmin,2.5rem)] font-black leading-none">
              {penalites >= 3 ? "H" : "S"}
            </div>

            <div className={`${FS.shidoValue} font-black leading-none`}>
              {penalites}
            </div>
          </div>
        </Pressable>
      </div>
    );
  };

  const FighterRow = ({
    fighter,
    competitor,
    score,
    penalites,
    osaekomi,
    isRed,
  }) => {
    const rowClass = isRed ? "bg-red-700 text-white" : "bg-white text-black";

    const running = fighter === 1 ? osaekomiRunning1 : osaekomiRunning2;

    return (
      <div
        className={`flex h-[30%] w-full flex-row items-center justify-between px-5 sm:px-8 ${rowClass}`}
      >
        <div className="flex min-w-[230px] max-w-[600px] flex-col justify-center w-[29%]">
          <div className={`${FS.nameLast} uppercase leading-none`}>
            {competitor?.nom_competiteur || (isRed ? "ROUGE" : "BLANC")}
          </div>

          {!!competitor?.prenom_competiteur && (
            <div
              className={`${FS.nameFirst} mt-2 uppercase leading-none font-semibold opacity-90`}
            >
              {competitor.prenom_competiteur}
            </div>
          )}

          {!!competitor?.club_competiteur && (
            <div
              className={`${FS.club} mt-4 uppercase leading-none font-semibold opacity-80`}
            >
              {competitor.club_competiteur}
            </div>
          )}
        </div>

        <div className="flex h-full flex-1 items-center justify-end">
          <div className="flex h-[80%] items-center space-x-[clamp(1rem,3.3vmin,3.6rem)]">
            <ScoreValue
              fighter={fighter}
              scoreKey="ippon"
              value={score?.ippon || 0}
              isRed={isRed}
            />

            <ScoreValue
              fighter={fighter}
              scoreKey="wazari"
              value={score?.wazari || 0}
              isRed={isRed}
            />

            <ScoreValue
              fighter={fighter}
              scoreKey="yuko"
              value={score?.yuko || 0}
              isRed={isRed}
            />
          </div>
        </div>

        <OsaekomiAndShido
          fighter={fighter}
          osaekomi={osaekomi}
          isRunning={running}
          penalites={penalites}
          isRed={isRed}
        />
      </div>
    );
  };

  const ActionButton = ({
    children,
    onClick,
    icon,
    variant = "default",
    title = "",
  }) => {
    const variants = {
      default:
        "border-white/15 bg-white/10 text-white hover:bg-white/15 active:bg-white/20",
      green:
        "border-emerald-300/40 bg-emerald-500/20 text-emerald-100 hover:bg-emerald-500/30",
      red: "border-red-300/40 bg-red-500/20 text-red-100 hover:bg-red-500/30",
      blue: "border-blue-300/40 bg-blue-500/20 text-blue-100 hover:bg-blue-500/30",
      yellow:
        "border-yellow-300/40 bg-yellow-500/20 text-yellow-100 hover:bg-yellow-500/30",
      purple:
        "border-purple-300/40 bg-purple-500/20 text-purple-100 hover:bg-purple-500/30",
    };

    return (
      <button
        type="button"
        title={title}
        onClick={onClick}
        className={`inline-flex items-center justify-center gap-2 rounded-2xl border px-4 py-3 text-[clamp(.85rem,1.55vmin,1.2rem)] font-black shadow-xl backdrop-blur transition outline-none focus:outline-none focus-visible:outline-none focus:ring-0 ${variants[variant]}`}
      >
        {icon}
        <span>{children}</span>
      </button>
    );
  };

  return (
    <div
      ref={rootRef}
      className={`relative w-full overflow-hidden bg-black text-white shadow-2xl ${
        isFullscreen
          ? "h-screen min-h-screen rounded-none"
          : "h-[calc(100vh-150px)] min-h-[720px]"
      }`}
    >
      <div className="flex h-full w-full flex-col font-black">
        {/* Bandeau haut */}
        <div className="flex h-[10%] items-center justify-between bg-black px-5 text-white sm:px-8">
          <div className="flex min-w-0 items-center gap-4">
            <img
              src={APP_LOGO_URL}
              alt="MonAppliTournoi"
              className="hidden h-[clamp(2.8rem,6vmin,5.4rem)] w-[clamp(2.8rem,6vmin,5.4rem)] rounded-2xl object-cover sm:block"
              onError={(e) => {
                e.currentTarget.style.display = "none";
              }}
            />

            <div className={`${FS.topTitle} truncate`}>MonAppliTournoi</div>
          </div>

          <div className={`${FS.topTapis} leading-none text-yellow-300`}>
            TAPIS {tapisLabel}
          </div>
        </div>

        {/* Rouge */}
        <FighterRow
          fighter={1}
          competitor={redCompetitor}
          score={score1}
          penalites={penalites1}
          osaekomi={osaekomi1}
          isRed
        />

        {/* Blanc */}
        <FighterRow
          fighter={2}
          competitor={whiteCompetitor}
          score={score2}
          penalites={penalites2}
          osaekomi={osaekomi2}
          isRed={false}
        />

        {/* Bas */}
        <div className="flex h-[30%] w-full items-center justify-between bg-black px-5 text-white sm:px-8">
          <div className="flex max-w-[42%] flex-col gap-4">
            <div className="grid grid-cols-2 gap-3">
              <ActionButton
                icon={<FaSlidersH />}
                variant="blue"
                onClick={() => setControlsOpen((v) => !v)}
              >
                Commandes
              </ActionButton>

              <ActionButton
                icon={<FaFlagCheckered />}
                variant="purple"
                onClick={() => setModalVisible(true)}
              >
                Vainqueur
              </ActionButton>

              <ActionButton
                icon={<FaRedo />}
                variant="red"
                onClick={resetFight}
              >
                Réinitialiser
              </ActionButton>

              <ActionButton
                icon={isFullscreen ? <FaCompress /> : <FaExpand />}
                variant="yellow"
                onClick={toggleFullscreen}
              >
                {isFullscreen ? "Mode normal" : "Plein écran"}
              </ActionButton>
            </div>

            <div className="text-[clamp(.85rem,1.6vmin,1.2rem)] font-bold text-white/45">
              Clic court : ajouter / démarrer • appui long : retirer / remettre
              à zéro
            </div>
            <div className="text-[clamp(.85rem,1.6vmin,1.2rem)] font-bold text-white/45">
              Chronomètre principal barre d'espace : start / stop
            </div>
          </div>

          <div className="flex w-full max-w-[64%] flex-col items-end justify-center">
            <button
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={toggleMainChrono}
              tabIndex={-1}
              className={`rounded-3xl px-6 py-2 text-right transition outline-none focus:outline-none focus-visible:outline-none focus:ring-0 ${
                isChronoRunning
                  ? "bg-emerald-500/10 text-emerald-300"
                  : "hover:bg-white/10"
              }`}
              title="Clic ou barre espace : démarrer / arrêter le chrono"
            >
              <div
                className={`${FS.chrono} font-sans tabular-nums leading-none ${
                  goldenScoreMode ? "text-yellow-400" : "text-white"
                }`}
              >
                {goldenScoreMode
                  ? `GS ${formatTime(goldenChrono)}`
                  : formatTime(chrono)}
              </div>
            </button>

            {!goldenScoreMode && chronoMax && (
              <div className="mt-[clamp(1.2rem,3vmin,2rem)] h-[clamp(.75rem,1.6vmin,1.4rem)] w-full overflow-hidden rounded bg-gray-700">
                <div
                  className="h-full bg-green-400 transition-all duration-300"
                  style={{ width: `${chronoProgress}%` }}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Panneau commandes secondaires */}
      {controlsOpen && (
        <div className="absolute inset-x-4 bottom-[clamp(7rem,16vh,11rem)] z-40 rounded-3xl border border-white/10 bg-slate-950/95 p-5 shadow-2xl backdrop-blur">
          <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <div className="text-xl font-black">Commandes secondaires</div>
              <div className="text-sm font-semibold text-white/45">
                Ajustement chrono, gong, forfait, golden score et désignation du
                vainqueur.
              </div>
            </div>

            <button
              type="button"
              onClick={() => setControlsOpen(false)}
              className="inline-flex items-center justify-center gap-2 rounded-2xl border border-white/10 bg-white/10 px-4 py-3 text-sm font-black text-white outline-none hover:bg-white/15 focus:outline-none focus-visible:outline-none focus:ring-0"
            >
              <FaTimes />
              Fermer
            </button>
          </div>

          <div className="grid gap-4 lg:grid-cols-3">
            <div className="rounded-2xl border border-white/10 bg-black/40 p-4">
              <div className="mb-3 text-sm font-black uppercase tracking-wide text-white/60">
                Chrono principal
              </div>

              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                <ActionButton
                  icon={isChronoRunning ? <FaStop /> : <FaPlay />}
                  variant="green"
                  onClick={toggleMainChrono}
                >
                  {isChronoRunning ? "Stop" : "Start"}
                </ActionButton>

                <ActionButton
                  icon={<FaPlus />}
                  variant="default"
                  onClick={() => adjustChrono(10)}
                >
                  +10 sec
                </ActionButton>

                <ActionButton
                  icon={<FaMinus />}
                  variant="default"
                  onClick={() => adjustChrono(-10)}
                >
                  -10 sec
                </ActionButton>

                <ActionButton
                  icon={<FaRedo />}
                  variant="red"
                  onClick={() => {
                    setChrono(chronoMax);
                    setIsChronoRunning(false);
                    chronoEndedRef.current = false;
                    autoEndRef.current = false;
                  }}
                >
                  Reset chrono
                </ActionButton>

                <ActionButton
                  icon={<FaBell />}
                  variant="yellow"
                  onClick={playGongSound}
                >
                  Gong
                </ActionButton>

                <ActionButton
                  icon={<FaFlagCheckered />}
                  variant="purple"
                  onClick={() => setModalVisible(true)}
                >
                  Vainqueur
                </ActionButton>
              </div>
            </div>

            <div className="rounded-2xl border border-white/10 bg-black/40 p-4">
              <div className="mb-3 text-sm font-black uppercase tracking-wide text-white/60">
                Golden score
              </div>

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <ActionButton
                  icon={goldenScoreMode ? <FaStop /> : <FaPlay />}
                  variant={goldenScoreMode ? "yellow" : "blue"}
                  onClick={() => {
                    setGoldenScoreMode((v) => !v);
                    setIsChronoRunning(false);
                    autoEndRef.current = false;
                  }}
                >
                  {goldenScoreMode ? "Quitter GS" : "Activer GS"}
                </ActionButton>

                <ActionButton
                  icon={<FaRedo />}
                  variant="default"
                  onClick={() => {
                    setGoldenChrono(0);
                    setIsChronoRunning(false);
                  }}
                >
                  Reset GS
                </ActionButton>
              </div>

              <div className="mt-4 rounded-2xl border border-white/10 bg-white/5 p-3 text-sm font-semibold text-white/55">
                En Golden Score, le chrono monte au lieu de descendre.
              </div>
            </div>

            <div className="rounded-2xl border border-white/10 bg-black/40 p-4">
              <div className="mb-3 text-sm font-black uppercase tracking-wide text-white/60">
                Forfait / actions globales
              </div>

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <ActionButton
                  icon={<FaFlagCheckered />}
                  variant="red"
                  onClick={() => handleForfait(1)}
                >
                  Forfait rouge
                </ActionButton>

                <ActionButton
                  icon={<FaFlagCheckered />}
                  variant="red"
                  onClick={() => handleForfait(2)}
                >
                  Forfait blanc
                </ActionButton>

                <ActionButton
                  icon={<FaRedo />}
                  variant="red"
                  onClick={resetFight}
                >
                  Tout réinitialiser
                </ActionButton>

                <ActionButton
                  icon={isFullscreen ? <FaCompress /> : <FaExpand />}
                  variant="yellow"
                  onClick={toggleFullscreen}
                >
                  {isFullscreen ? "Mode normal" : "Plein écran"}
                </ActionButton>
              </div>
            </div>
          </div>
        </div>
      )}

      <DetermineWinnerModal
        isVisible={modalVisible}
        onClose={() => setModalVisible(false)}
        score1={score1}
        score2={score2}
        competiteur1={redCompetitor}
        competiteur2={whiteCompetitor}
        penalites1={penalites1}
        penalites2={penalites2}
        onValidate={(payload) => {
          console.log("Résultat combat visiteur :", payload);
          toast.success("Résultat validé !");
        }}
      />

      <ToastContainer position="bottom-left" theme="dark" />
    </div>
  );
};

export default ScoreboardUser;
